import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Plus, Edit, Trash2, X, Save, BookOpen, Upload, Music, Code, FileText, Check, Lock, Unlock } from 'lucide-react';

export default function ManageMeditations() {
  const { token } = useAuth();
  const { currentColors } = useTheme();
  const [meditations, setMeditations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const audioInputRef = useRef(null);
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: 10,
    category: '',
    script_text: '',
    custom_html: '',
    use_custom_html: false,
    audio_url: '',
    image_url: '',
    is_premium: false
  });

  useEffect(() => {
    loadMeditations();
  }, []);

  const loadMeditations = async () => {
    try {
      const response = await axios.get(`${API}/meditations`);
      setMeditations(response.data);
    } catch (error) {
      console.error('Failed to load meditations:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Prepare data - if using custom HTML, set a placeholder for script_text
      const submitData = {
        ...formData,
        script_text: formData.use_custom_html ? (formData.script_text || 'Custom HTML meditation') : formData.script_text
      };
      
      if (editingId) {
        await axios.put(
          `${API}/meditations/${editingId}`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/meditations`,
          submitData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      resetForm();
      loadMeditations();
    } catch (error) {
      console.error('Failed to save meditation:', error);
      alert('Failed to save meditation: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (meditation) => {
    setFormData({
      title: meditation.title,
      description: meditation.description,
      duration: meditation.duration,
      category: meditation.category,
      script_text: meditation.script_text,
      custom_html: meditation.custom_html || '',
      use_custom_html: meditation.use_custom_html || false,
      audio_url: meditation.audio_url || '',
      image_url: meditation.image_url || '',
      is_premium: meditation.is_premium || false
    });
    setEditingId(meditation.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this meditation?')) return;
    
    try {
      await axios.delete(`${API}/meditations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMeditations();
    } catch (error) {
      console.error('Failed to delete meditation:', error);
      alert('Failed to delete meditation');
    }
  };

  const handleAudioUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadProgress('Uploading audio...');
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload/audio`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // Use functional update to avoid stale closure
      setFormData(prev => ({ ...prev, audio_url: response.data.url }));
      setUploadProgress(`Audio uploaded! URL: ${response.data.url}`);
    } catch (error) {
      console.error('Failed to upload audio:', error);
      setUploadProgress('Failed to upload audio: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(''), 5000);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setUploadProgress('Uploading image...');
    
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API}/upload/image`, formDataUpload, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // Use functional update to avoid stale closure
      setFormData(prev => ({ ...prev, image_url: response.data.url }));
      setUploadProgress(`Image uploaded! URL: ${response.data.url}`);
    } catch (error) {
      console.error('Failed to upload image:', error);
      setUploadProgress('Failed to upload image: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(''), 5000);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      duration: 10,
      category: '',
      script_text: '',
      custom_html: '',
      use_custom_html: false,
      audio_url: '',
      image_url: '',
      is_premium: false
    });
    setEditingId(null);
    setShowForm(false);
    setUploadProgress('');
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <BookOpen className="w-6 h-6" style={{ color: currentColors.primary }} />
          <h2 className="text-2xl font-cinzel" style={{ color: currentColors.primary }}>
            Manage Meditations
          </h2>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="cosmic-btn flex items-center gap-2"
        >
          {showForm ? <X className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
          <span>{showForm ? 'Cancel' : 'Add New'}</span>
        </button>
      </div>

      {showForm && (
        <div className="glass-card p-6">
          <h3 className="text-xl font-cinzel mb-4" style={{ color: currentColors.text }}>
            {editingId ? 'Edit Meditation' : 'Add New Meditation'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="cosmic-input w-full"
                  required
                />
              </div>
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Category *</label>
                <input
                  type="text"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="cosmic-input w-full"
                  placeholder="e.g., Morning, Sleep, Stress"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="cosmic-input w-full"
                rows="2"
                required
              />
            </div>

            <div>
              <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Duration (minutes) *</label>
              <input
                type="number"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
                className="cosmic-input w-full"
                min="1"
                required
              />
            </div>

            {/* Content Type Toggle */}
            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: `${currentColors.primary}10`, border: `1px solid ${currentColors.primary}30` }}
            >
              <label className="block text-sm mb-3" style={{ color: currentColors.text }}>Content Type</label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, use_custom_html: false })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all ${
                    !formData.use_custom_html ? 'ring-2' : 'opacity-60'
                  }`}
                  style={{ 
                    backgroundColor: !formData.use_custom_html ? `${currentColors.primary}20` : `${currentColors.textMuted}10`,
                    color: !formData.use_custom_html ? currentColors.primary : currentColors.textMuted,
                    ringColor: currentColors.primary
                  }}
                >
                  <FileText className="w-5 h-5" />
                  <span>Text Script</span>
                  {!formData.use_custom_html && <Check className="w-4 h-4" />}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, use_custom_html: true })}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all ${
                    formData.use_custom_html ? 'ring-2' : 'opacity-60'
                  }`}
                  style={{ 
                    backgroundColor: formData.use_custom_html ? `${currentColors.accent}20` : `${currentColors.textMuted}10`,
                    color: formData.use_custom_html ? currentColors.accent : currentColors.textMuted,
                    ringColor: currentColors.accent
                  }}
                >
                  <Code className="w-5 h-5" />
                  <span>Custom HTML</span>
                  {formData.use_custom_html && <Check className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Text Script */}
            {!formData.use_custom_html && (
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>Meditation Script *</label>
                <textarea
                  value={formData.script_text}
                  onChange={(e) => setFormData({ ...formData, script_text: e.target.value })}
                  className="cosmic-input w-full"
                  rows="6"
                  placeholder="Enter the full meditation script text..."
                  required={!formData.use_custom_html}
                />
              </div>
            )}

            {/* Custom HTML Editor */}
            {formData.use_custom_html && (
              <div>
                <label className="block text-sm mb-1" style={{ color: currentColors.textMuted }}>
                  Custom HTML Content *
                  <span className="ml-2 text-xs" style={{ color: currentColors.accent }}>
                    (Supports timer elements, animations, etc.)
                  </span>
                </label>
                <textarea
                  value={formData.custom_html}
                  onChange={(e) => setFormData({ ...formData, custom_html: e.target.value })}
                  className="cosmic-input w-full font-mono text-sm"
                  rows="10"
                  placeholder='<div class="meditation-content">
  <h2>Welcome to this guided meditation</h2>
  <div class="timer" data-duration="300">5:00</div>
  <p>Close your eyes and take a deep breath...</p>
</div>'
                  required={formData.use_custom_html}
                />
                <p className="text-xs mt-1" style={{ color: currentColors.textMuted }}>
                  You can use HTML, inline CSS, and data attributes for interactive elements.
                </p>
              </div>
            )}

            {/* Audio Upload */}
            <div 
              className="p-4 rounded-lg"
              style={{ backgroundColor: `${currentColors.secondary}10`, border: `1px solid ${currentColors.secondary}30` }}
            >
              <label className="block text-sm mb-2 flex items-center gap-2" style={{ color: currentColors.text }}>
                <Music className="w-4 h-4" style={{ color: currentColors.secondary }} />
                Audio File (MP3)
              </label>
              <div className="flex gap-2 items-center">
                <input
                  ref={audioInputRef}
                  type="file"
                  accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg"
                  onChange={handleAudioUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => audioInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
                  style={{ 
                    backgroundColor: `${currentColors.secondary}20`,
                    color: currentColors.secondary,
                    opacity: uploading ? 0.5 : 1
                  }}
                >
                  <Upload className="w-4 h-4" />
                  Upload MP3
                </button>
                {formData.audio_url && (
                  <span className="flex items-center gap-2 text-sm" style={{ color: currentColors.text }}>
                    <Check className="w-4 h-4 text-green-500" />
                    Audio uploaded
                  </span>
                )}
              </div>
              {formData.audio_url && (
                <div className="mt-2">
                  <audio controls className="w-full h-10" style={{ filter: 'sepia(20%) saturate(70%)' }}>
                    <source src={formData.audio_url.startsWith('http') ? formData.audio_url : `${API.replace('/api', '')}${formData.audio_url}`} type="audio/mpeg" />
                  </audio>
                </div>
              )}
              <p className="text-xs mt-2" style={{ color: currentColors.textMuted }}>
                Or enter URL manually:
              </p>
              <input
                type="url"
                value={formData.audio_url}
                onChange={(e) => setFormData({ ...formData, audio_url: e.target.value })}
                className="cosmic-input w-full mt-1"
                placeholder="https://example.com/audio.mp3"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm mb-2 flex items-center gap-2" style={{ color: currentColors.textMuted }}>
                Cover Image
              </label>
              <div className="flex gap-2 items-center mb-2">
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg transition-all"
                  style={{ 
                    backgroundColor: `${currentColors.accent}20`,
                    color: currentColors.accent,
                    opacity: uploading ? 0.5 : 1
                  }}
                >
                  <Upload className="w-4 h-4" />
                  Upload Image
                </button>
                {formData.image_url && (
                  <span className="flex items-center gap-2 text-sm" style={{ color: currentColors.text }}>
                    <Check className="w-4 h-4 text-green-500" />
                    Image uploaded
                  </span>
                )}
              </div>
              <input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                className="cosmic-input w-full"
                placeholder="https://images.unsplash.com/..."
              />
            </div>

            {/* Upload Progress */}
            {uploadProgress && (
              <div 
                className="text-sm text-center py-2 rounded"
                style={{ 
                  backgroundColor: uploadProgress.includes('success') ? 'rgba(34, 197, 94, 0.2)' : `${currentColors.primary}20`,
                  color: uploadProgress.includes('success') ? '#22c55e' : currentColors.primary
                }}
              >
                {uploadProgress}
              </div>
            )}

            {/* Premium/Free Toggle */}
            <div 
              className="p-4 rounded-lg flex items-center justify-between"
              style={{ 
                backgroundColor: formData.is_premium ? `${currentColors.accent}10` : 'rgba(34, 197, 94, 0.1)',
                border: `1px solid ${formData.is_premium ? `${currentColors.accent}30` : 'rgba(34, 197, 94, 0.3)'}`
              }}
            >
              <div className="flex items-center gap-3">
                {formData.is_premium ? (
                  <Lock className="w-5 h-5" style={{ color: currentColors.accent }} />
                ) : (
                  <Unlock className="w-5 h-5 text-green-500" />
                )}
                <div>
                  <p className="font-medium" style={{ color: currentColors.text }}>
                    {formData.is_premium ? 'Premium Content' : 'Free Content'}
                  </p>
                  <p className="text-xs" style={{ color: currentColors.textMuted }}>
                    {formData.is_premium 
                      ? 'Only users with premium access can view this meditation' 
                      : 'This meditation is available to all users'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_premium: !formData.is_premium })}
                className={`relative w-14 h-7 rounded-full transition-all`}
                style={{ 
                  backgroundColor: formData.is_premium ? currentColors.accent : '#22c55e'
                }}
              >
                <div 
                  className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-all ${
                    formData.is_premium ? 'right-1' : 'left-1'
                  }`}
                />
              </button>
            </div>

            <button type="submit" className="cosmic-btn w-full flex items-center justify-center gap-2">
              <Save className="w-5 h-5" />
              <span>{editingId ? 'Update Meditation' : 'Create Meditation'}</span>
            </button>
          </form>
        </div>
      )}

      <div className="glass-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr style={{ borderBottom: `1px solid ${currentColors.border}` }}>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Title</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Category</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Duration</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Type</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Access</th>
                <th className="text-left py-3 px-4 text-sm font-cinzel" style={{ color: currentColors.primary }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {meditations.map((meditation) => (
                <tr 
                  key={meditation.id} 
                  style={{ borderBottom: `1px solid ${currentColors.border}` }}
                  className="hover:opacity-80"
                >
                  <td className="py-3 px-4">
                    <div>
                      <p style={{ color: currentColors.text }}>{meditation.title}</p>
                      <p className="text-sm line-clamp-1" style={{ color: currentColors.textMuted }}>{meditation.description}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span 
                      className="px-3 py-1 text-xs rounded-full"
                      style={{ 
                        backgroundColor: `${currentColors.secondary}20`,
                        color: currentColors.secondary
                      }}
                    >
                      {meditation.category}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm" style={{ color: currentColors.textMuted }}>
                    {meditation.duration} min
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1">
                      {meditation.use_custom_html && (
                        <span 
                          className="px-2 py-0.5 text-xs rounded flex items-center gap-1"
                          style={{ backgroundColor: `${currentColors.accent}20`, color: currentColors.accent }}
                          title="Custom HTML"
                        >
                          <Code className="w-3 h-3" />
                        </span>
                      )}
                      {meditation.audio_url && (
                        <span 
                          className="px-2 py-0.5 text-xs rounded flex items-center gap-1"
                          style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}
                          title="Has Audio"
                        >
                          <Music className="w-3 h-3" />
                        </span>
                      )}
                      {!meditation.use_custom_html && !meditation.audio_url && (
                        <span className="text-xs" style={{ color: currentColors.textMuted }}>Text</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    {meditation.is_premium ? (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: `${currentColors.accent}20`, color: currentColors.accent }}>
                        <Lock className="w-3 h-3" /> Premium
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }}>
                        <Unlock className="w-3 h-3" /> Free
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(meditation)}
                        className="p-2 rounded-lg transition-all"
                        style={{ color: currentColors.accent }}
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(meditation.id)}
                        className="p-2 rounded-lg transition-all text-red-400 hover:bg-red-500/10"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {meditations.length === 0 && (
                <tr>
                  <td colSpan="6" className="py-8 text-center" style={{ color: currentColors.textMuted }}>
                    No meditations found. Add your first meditation!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
